import { DocumentPipelineDay } from '@numeo/types-core'

/** 95 → "1m 35s", 7300 → "2h 1m". */
export function formatDuration(seconds: number | null): string {
    if (seconds === null) return '—'
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`
    const hours = Math.floor(minutes / 60)
    if (hours < 48) return `${hours}h ${minutes % 60}m`
    return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

export function percent(part: number, whole: number): string {
    return whole ? `${Math.round((part / whole) * 1000) / 10}%` : '—'
}

/** `2026-10-07` → "Wed, Oct 7, 2026": an audit day as a US date. A day has no time of day, so nothing is shifted into a zone. */
export function formatAuditDay(day: string): string {
    const [year, month, date] = day.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, date, 12)).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    })
}

/** One day's counts, for its summary cards and mix tables; all zero for a day with no jobs. */
export function dayTotals(day: DocumentPipelineDay | undefined) {
    const totals = {
        enqueued: 0,
        done: 0,
        notOfInterest: 0,
        combined: 0,
        failed: 0,
        inFlight: 0,
        deduplicated: 0,
        textRoute: 0,
        visualRoute: 0,
        resultHandlerFailed: 0,
        shadowTotal: 0,
        shadowCompared: 0,
        shadowAgreed: 0,
        shadowScoreSum: 0,
        shadowLegacyPending: 0,
        shadowLegacyNone: 0,
        shadowAgreeNone: 0,
        shadowLegacyOnly: 0,
        shadowLegacyOnlyNoRate: 0,
        shadowLegacyUnknown: 0,
        byDocType: {} as Record<string, number>,
        byOcrAdapter: {} as Record<string, number>,
        llmInputTokens: 0,
        llmOutputTokens: 0,
    }
    for (const d of day ? [day] : []) {
        // Absent on a main-service that predates token tracking.
        totals.llmInputTokens += d.llm?.inputTokens ?? 0
        totals.llmOutputTokens += d.llm?.outputTokens ?? 0
        totals.enqueued += d.enqueued
        totals.done += d.done
        totals.notOfInterest += d.notOfInterest
        totals.combined += d.combined ?? 0
        totals.failed += d.failed
        totals.inFlight += d.inFlight
        totals.deduplicated += d.deduplicated
        totals.textRoute += d.textRoute
        totals.visualRoute += d.visualRoute
        totals.resultHandlerFailed += d.resultHandlerFailed
        totals.shadowTotal += d.shadow.total
        totals.shadowCompared += d.shadow.compared
        totals.shadowAgreed += d.shadow.agreed
        totals.shadowScoreSum += (d.shadow.averageScore ?? 0) * d.shadow.compared
        totals.shadowLegacyPending += d.shadow.legacyPending
        totals.shadowLegacyNone += d.shadow.legacyNone
        totals.shadowAgreeNone += d.shadow.agreeNone
        // Absent on a main-service that predates recording both directions.
        totals.shadowLegacyOnly += d.shadow.legacyOnly ?? 0
        totals.shadowLegacyOnlyNoRate += d.shadow.legacyOnlyNoRate ?? 0
        totals.shadowLegacyUnknown += d.shadow.legacyUnknown ?? 0
        for (const [k, v] of Object.entries(d.byDocType)) totals.byDocType[k] = (totals.byDocType[k] ?? 0) + v
        for (const [k, v] of Object.entries(d.byOcrAdapter)) totals.byOcrAdapter[k] = (totals.byOcrAdapter[k] ?? 0) + v
    }
    return totals
}
